import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { approveFinding, needsReviewFinding, rejectFinding } from '@/api/findings';
import type { ApprovalReason, FindingOut, ReasonCode } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { applyPrefill } from '@/lib/approval-reasons';
import { ApprovalReasonPicker } from '@/components/ApprovalReasonPicker';
import { ReasonCodesPicker } from '@/components/ReasonCodesPicker';
import { Button } from '@/components/ui/button';

/** Reject takes EITHER a free-text reason OR one+ structured codes (or
 *  both) — the backend's FindingRejectIn 422s a request with neither.
 *  Exported so the guard can be unit-tested without rendering. */
export function canSubmitRejection(note: string, codes: readonly ReasonCode[]): boolean {
  return note.trim().length > 0 || codes.length > 0;
}

/** Approve / Reject / Needs-review controls for a single finding, shared by
 *  the surfaces that disposition one finding at a time (the finding detail
 *  page today). Renders nothing for non-practitioners. Re-review is allowed
 *  on an already-dispositioned finding — the backend permits the transition.
 */
export function FindingDispositionPanel({
  finding,
  runId,
  canReview,
  defaultApprovalReason,
  onDispositioned,
}: {
  finding: FindingOut;
  runId: string;
  canReview: boolean;
  /** Prefill for the approval-basis picker — the host passes
   *  `invoice_confirmed` when the transaction has an attached invoice showing
   *  tax, so the common case is one confirm click. Undefined forces a
   *  conscious pick. */
  defaultApprovalReason?: ApprovalReason;
  /** Fires after any successful disposition, so a host surface with its
   *  own query (e.g. the supplier view) can refresh alongside the shared
   *  run/list queries this component already invalidates. */
  onDispositioned?: () => void;
}) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<null | 'approve' | 'reject' | 'needs_review'>(null);
  const [note, setNote] = useState('');
  const [rejectCodes, setRejectCodes] = useState<ReasonCode[]>([]);
  const [approveReason, setApproveReason] = useState<ApprovalReason | null>(
    defaultApprovalReason ?? null,
  );
  // Invoice links load after this panel mounts, so the prefill can arrive a
  // tick late. Apply it once available — but only while the reviewer hasn't
  // picked yet, so a deliberate choice is never overwritten.
  useEffect(() => {
    setApproveReason((current) => applyPrefill(current, defaultApprovalReason));
  }, [defaultApprovalReason]);

  const invalidate = () => {
    // The detail page renders from the load-by-id query; the run list +
    // events power the other surfaces. Refresh all so the new status shows
    // everywhere without a manual reload.
    qc.invalidateQueries({ queryKey: ['finding', finding.finding_id, userKey] });
    qc.invalidateQueries({ queryKey: ['findings', 'by-run', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['runs', runId, userKey] });
    qc.invalidateQueries({ queryKey: ['finding-events', finding.finding_id] });
    onDispositioned?.();
  };
  const reset = () => {
    setNote('');
    setRejectCodes([]);
    setApproveReason(defaultApprovalReason ?? null);
    setExpanded(null);
  };

  const approve = useMutation({
    mutationFn: async () => {
      if (approveReason === null) throw new Error('an approval basis is required');
      return approveFinding(
        finding.finding_id,
        (await getIdToken()) ?? '',
        approveReason,
        note.trim() || undefined,
      );
    },
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const reject = useMutation({
    mutationFn: async () =>
      rejectFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? '', rejectCodes),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const needsReview = useMutation({
    mutationFn: async () =>
      needsReviewFinding(finding.finding_id, note.trim(), (await getIdToken()) ?? ''),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });

  // `anyError` is shared across the three independent mutations, so clear
  // all of their states when the practitioner starts a new action —
  // otherwise a failed reject would keep the banner up after a later
  // approve succeeds.
  const resetMutations = () => {
    approve.reset();
    reject.reset();
    needsReview.reset();
  };

  if (!canReview) return null;

  const busy = approve.isPending || reject.isPending || needsReview.isPending;
  const anyError = approve.isError || reject.isError || needsReview.isError;
  const canReject = canSubmitRejection(note, rejectCodes);
  const canFlag = note.trim().length > 0;
  const canApprove = approveReason !== null;

  return (
    <section className="grid gap-2 rounded-md border border-border bg-muted/30 px-3 py-3">
      <p className="text-xs uppercase tracking-wide text-brand-slate">Review</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="successOutline"
          className="h-8"
          disabled={busy}
          onClick={() => {
            resetMutations();
            setExpanded((e) => (e === 'approve' ? null : 'approve'));
          }}
          title="Approve this finding"
        >
          Approve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 border-rose-500/40 text-rose-700 hover:bg-rose-50"
          disabled={busy}
          onClick={() => {
            resetMutations();
            setExpanded((e) => (e === 'reject' ? null : 'reject'));
          }}
          title="Reject this finding"
        >
          Reject
        </Button>
        <Button
          type="button"
          size="sm"
          variant="cautionOutline"
          className="h-8"
          disabled={busy}
          onClick={() => {
            resetMutations();
            setExpanded((e) => (e === 'needs_review' ? null : 'needs_review'));
          }}
          title="Flag this finding for follow-up"
        >
          Flag for review
        </Button>
      </div>

      {anyError && (
        <p className="text-xs text-destructive">
          Action failed — the finding wasn&apos;t updated. Check your connection and try
          again.
        </p>
      )}

      {expanded !== null && (
        <form
          className="grid gap-2 rounded-md border border-border bg-background p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (expanded === 'approve') {
              if (!canApprove) return;
              void approve.mutate();
            } else if (expanded === 'reject') {
              if (!canReject) return;
              void reject.mutate();
            } else {
              if (!canFlag) return;
              void needsReview.mutate();
            }
          }}
        >
          {expanded === 'approve' && (
            <ApprovalReasonPicker value={approveReason} onChange={setApproveReason} disabled={busy} />
          )}
          {expanded === 'reject' && (
            <ReasonCodesPicker value={rejectCodes} onChange={setRejectCodes} />
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              expanded === 'approve'
                ? 'Optional note'
                : expanded === 'reject'
                  ? 'Optional when codes are picked — otherwise required'
                  : "Why can't this be decided yet? (required)"
            }
            maxLength={256}
            disabled={busy}
            className="min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
          />
          <div className="flex items-center justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={reset} disabled={busy}>
              Cancel
            </Button>
            {expanded === 'approve' ? (
              <Button type="submit" size="sm" disabled={busy || !canApprove}>
                {approve.isPending ? 'Approving…' : 'Approve'}
              </Button>
            ) : expanded === 'reject' ? (
              <Button type="submit" size="sm" variant="destructive" disabled={busy || !canReject}>
                {reject.isPending ? 'Rejecting…' : 'Reject'}
              </Button>
            ) : (
              <Button type="submit" size="sm" disabled={busy || !canFlag}>
                {needsReview.isPending ? 'Flagging…' : 'Flag for review'}
              </Button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
